<script lang="ts">
	import { Copy, Check, Download, Eye, EyeOff } from 'lucide-svelte';
	
	// Phase 6: Svelte 5 props! 🚀
	let { xml, releaseId = 'release' }: { xml: string; releaseId?: string } = $props();
	
	// Phase 6: Svelte 5 component state! 🚀
	let copied = $state(false);
	let collapsed = $state(false);
	
	// Format XML with proper indentation
	function formatXML(xml: string): string {
		const PADDING = '  '; // 2 spaces for indentation
		const reg = /(>)(<)(\/*)/g;
		let pad = 0;
		
		xml = xml.replace(reg, '$1\r\n$2$3');
		
		return xml.split('\r\n').map((node, index) => {
			let indent = 0;
			if (node.match(/.+<\/\w[^>]*>$/)) {
				indent = 0;
			} else if (node.match(/^<\/\w/) && pad > 0) {
				pad -= 1;
			} else if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
				indent = 1;
			} else {
				indent = 0;
			}
			
			pad += indent;
			return PADDING.repeat(pad - indent) + node;
		}).join('\r\n');
	}
	
	// Syntax highlight XML
	function highlightXML(xml: string): string {
		// First escape HTML to prevent XSS, then apply syntax highlighting
		const escaped = xml
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
			
		return escaped
			// XML declaration and processing instructions
			.replace(/(&lt;\?xml.*?\?&gt;)/g, '<span class="xml-declaration">$1</span>')
			// Comments
			.replace(/(&lt;!--.*?--&gt;)/gs, '<span class="xml-comment">$1</span>')
			// Opening tags
			.replace(/(&lt;)([^\/\s&gt;]+)([^&gt;]*?)(&gt;)/g, '<span class="xml-bracket">$1</span><span class="xml-tag">$2</span><span class="xml-attr">$3</span><span class="xml-bracket">$4</span>')
			// Closing tags
			.replace(/(&lt;\/)([^&gt;]+)(&gt;)/g, '<span class="xml-bracket">$1</span><span class="xml-tag">$2</span><span class="xml-bracket">$3</span>')
			// Attribute values
			.replace(/(=&quot;)([^&]*?)(&quot;)/g, '=$1<span class="xml-value">$2</span>$3');
	}
	
	// Phase 6: Svelte 5 runes! 🚀
	let formattedXML = $derived(formatXML(xml));
	let highlightedXML = $derived(highlightXML(formattedXML));
	
	async function copyToClipboard() {
		try {
			await navigator.clipboard.writeText(formattedXML);
			copied = true;
			setTimeout(() => copied = false, 2000);
		} catch (err) {
			console.error('Failed to copy: ', err);
		}
	}
	
	function downloadXML() {
		const blob = new Blob([formattedXML], { type: 'application/xml' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `${releaseId}.xml`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}
</script>

<div class="xml-viewer bg-gray-50 rounded-xl border border-gray-200 overflow-hidden shadow-inner">
	<!-- Header with macOS-style controls -->
	<div class="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
		<div class="flex items-center space-x-3">
			<div class="w-3 h-3 bg-red-500 rounded-full"></div>
			<div class="w-3 h-3 bg-yellow-500 rounded-full"></div>
			<div class="w-3 h-3 bg-green-500 rounded-full"></div>
			<span class="text-sm font-semibold text-gray-700 ml-4 font-mono">{releaseId}.xml</span>
		</div>
		
		<div class="flex items-center space-x-2">
			<button
				class="btn btn-ghost btn-sm"
				onclick={() => collapsed = !collapsed}
				title={collapsed ? 'Expand' : 'Collapse'}
			>
				{#if collapsed}
					<Eye size={16} />
					<span class="hidden sm:inline">Expand</span>
				{:else}
					<EyeOff size={16} />
					<span class="hidden sm:inline">Collapse</span>
				{/if}
			</button>
			
			<button
				class="btn btn-ghost btn-sm"
				onclick={copyToClipboard}
				title="Copy to clipboard"
			>
				{#if copied}
					<Check size={16} class="text-green-600" />
					<span class="hidden sm:inline text-green-600">Copied!</span>
				{:else}
					<Copy size={16} />
					<span class="hidden sm:inline">Copy</span>
				{/if}
			</button>
			
			<button
				class="btn btn-ghost btn-sm"
				onclick={downloadXML}
				title="Download XML"
			>
				<Download size={16} />
				<span class="hidden sm:inline">Download</span>
			</button>
		</div>
	</div>
	
	<!-- XML Content -->
	{#if !collapsed}
		<div class="relative max-h-96 overflow-auto">
			<div class="flex">
				<!-- Line numbers -->
				<div class="w-16 bg-gray-100 border-r border-gray-200 text-xs text-gray-500 font-mono select-none flex-shrink-0">
					{#each formattedXML.split('\n') as _, i}
						<div class="px-3 py-1 text-right leading-6 hover:bg-gray-200 transition-colors">{i + 1}</div>
					{/each}
				</div>
				
				<!-- XML content with syntax highlighting -->
				<div class="flex-1 px-6 py-4 bg-white">
					<pre class="font-mono text-sm leading-6 text-gray-800">{@html highlightedXML}</pre>
				</div>
			</div>
		</div>
	{:else}
		<div class="p-8 text-center text-gray-500 bg-white">
			<div class="avatar avatar-md bg-gray-100 text-gray-500 mx-auto mb-4">
				<EyeOff size={24} />
			</div>
			<p class="text-lg font-medium mb-2">XML content collapsed</p>
			<p class="text-sm">{formattedXML.split('\n').length} lines • {Math.round(xml.length / 1024)}KB</p>
		</div>
	{/if}
</div>

<style>
	:global(.xml-viewer .xml-declaration) {
		color: rgb(147 51 234); /* purple-600 */
		font-weight: 600;
	}
	
	:global(.xml-viewer .xml-comment) {
		color: rgb(107 114 128); /* gray-500 */
		font-style: italic;
	}
	
	:global(.xml-viewer .xml-tag) {
		color: rgb(59 130 246); /* blue-500 */
		font-weight: 600;
	}
	
	:global(.xml-viewer .xml-bracket) {
		color: rgb(75 85 99); /* gray-600 */
	}
	
	:global(.xml-viewer .xml-attr) {
		color: rgb(16 185 129); /* emerald-500 */
	}
	
	:global(.xml-viewer .xml-value) {
		color: rgb(239 68 68); /* red-500 */
		font-weight: 500;
	}
</style>